import * as projection from "@arcgis/core/geometry/projection";
import SpatialReference from "@arcgis/core/geometry/SpatialReference";
import * as query from "@arcgis/core/rest/query";
import Query from "@arcgis/core/rest/support/Query";
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import KeyboardArrowUpIcon from '@mui/icons-material/KeyboardArrowUp';
import MenuIcon from '@mui/icons-material/Menu';
import { AppBar, Backdrop, Box, Button, CircularProgress, createTheme, Divider, IconButton, Typography } from "@mui/material";
import Alert from '@mui/material/Alert';
import Snackbar from '@mui/material/Snackbar';
import Toolbar from '@mui/material/Toolbar';
import React, { useEffect, useRef, useState } from "react";
import { emitCustomEvent } from 'react-custom-events';
import ReactDOM, { createPortal } from "react-dom";
import { useSelector } from "react-redux";
import { useNavigate } from "react-router";
import ColorConstants from "../defaults/ColorConstants";
import Events from '../defaults/Events';
import FieldNames from '../defaults/FieldNames.json';
import DetailedFundingSources from '../defaults/DetailedFundingSources.json';
import { useIFPRSGlobalContext } from "../IFPRSGlobalContext";
import logo from '../images/IFPRS_Logo.png';
import Request from '../services/request';
import ActionsMenu from './ActionsMenu';
import CreateMenu from "./CreateMenu";
import CreateActualsToolbar from "./esri/CreateActualsToolbar";
import CreatePerimeterToolbar from "./esri/CreatePerimeterToolbar";
import EditGeometryToolbar from "./esri/EditGeometryToolbar";
import InFormAlert from './InFormAlert';
import InFormGenericModal from './InFormGenericModal';
import AdminDialog from "./userPopover/AdminDialog";
import ProfileDialog from "./userPopover/ProfileDialog";
import WorkflowMenu from "./WorkflowMenu";
import packageInfo from '../../package.json';
import FilterAltIcon from '@mui/icons-material/FilterAlt';
import WorkflowFilterContainer from "./WorkflowFilterContainer";
import { appBarStyle } from "../utils/StyleHelper";
import {  ThemeProvider } from "@mui/material/styles";
import { makeStyles } from "@mui/styles";

const theme = createTheme({
  overrides: {
    MuiSnackbar: {
      root: {
        top: '94px !important'
      },
    },
  },
});

const useStyles = makeStyles(() => ({
  snackBarStyle: {
    top: '120px !important'
  },
}));

export default function Header({ isTreatment, isCreatingPlannedFeature, setIsCreatingPlannedFeature, 
  isCreatingActualFeature, setIsCreatingActualFeature, isEditingTreatment, setIsEditingTreatment, feature, setFeature, mode,
  rederiveFeatureInfo, isNewFeature, isApproved, isActuals, setIsTreatment,
  plannedFeature, userInfo, setUserInfo, userRoleInfo, setUserRoleInfo, logout, isCompleted, isWithinFiscalYear, derivationError, setDerivationError, derivationErrorOpen, setDerivationErrorOpen, headerHeight, setHeaderHeight,
  mapSnackbarMessage, setMapSnackbarMessage, mapSnackbarOpen, setMapSnackbarOpen, mapSnackbarSeverity, setMapSnackbarSeverity, setShowMap, isCreatingPostFire, setIsCreatingPostFire,
  isCreatingPostFireProject, setIsCreatingPostFireProject, isProject }) {

  const classes = useStyles();
  const navigate = useNavigate();
  const [anchorEl, setAnchorEl] = useState(null);
  const [browserWarningOpen, setBrowserWarningOpen] = useState(false);
  const [browserWarningAcknowledged, setBrowserWarningAcknowledged] = useState(false);
  const [anchorCreateMenuEl, setAnchorCreateMenuEl] = useState(null);
  const [anchorWorkflowMenuEl, setAnchorWorkflowMenuEl] = useState(null);
  const [editablePerimeter, setEditablePerimeter] = useState();
  const open = Boolean(anchorEl);
  const createOpen = Boolean(anchorCreateMenuEl);
  const workflowOpen = Boolean(anchorWorkflowMenuEl);
  const [isCreateUserOpen, setIsCreateUserOpen] = useState(false);
  const [isUserAdmin, setIsUserAdmin] = useState(false);
  const [isUserElevatedReporting, setIsUserElevatedReporting] = useState(false);
  const [isProfileOpen, setIsProfileOpen] = useState(false);
  const [serviceValidatorOpen,setServiceValidatorOpen] = useState(false);
  const [serviceValidatorParentStatus,setServiceValidatorParentStatus] = useState("loading");
  const [internalServiceUp,setInternalServiceUp] = useState("loading");
  const [jurisdictionalServiceUp,setJurisdictionalServiceUp] = useState("loading");
  const [gaccServiceUp,setGaccServiceUp] = useState("loading");
  const [stateServiceUp,setStateServiceUp] = useState("loading");
  const [countyServiceUp,setCountyServiceUp] = useState("loading");
  const [congDistrictServiceUp,setCongDistrictServiceUp] = useState("loading");
  const [biaServiceUp,setBiaServiceUp] = useState("loading");
  const [vdepImageServiceUp,setVdepImageServiceUp] = useState("loading");
  const [tribeServiceUp,setTribeServiceUp] = useState("loading");
  const [tribeLeaderServiceUp,setTribeLeaderServiceUp] = useState("loading");
  const [blmRegionServiceUp,setBlmRegionServiceUp] = useState("loading");
  const [borRegionServiceUp,setBorRegionServiceUp] = useState("loading");
  const [blmDistrictServiceUp,setBlmDistrictServiceUp] = useState("loading");
  const [blmFieldServiceUp,setBlmFieldServiceUp] = useState("loading");
  const [blmOtherServiceUp,setBlmOtherServiceUp] = useState("loading");
  const [fwsRegionServiceUp,setFwsRegionServiceUp] = useState("loading");
  const [fwsRefugeServiceUp,setFwsRefugeServiceUp] = useState("loading");
  const [doiUnifiedRegionServiceUp,setDoiUnifiedRegionServiceUp] = useState("loading");
  const [npsLegacyRegionsServiceUp,setNpsLegacyRegionsServiceUp] = useState("loading");
  const [npsFireManagementZoneServiceUp,setNpsFireManagementZoneServiceUp] = useState("loading");
  const [npsBoundaryServiceUp,setNpsBoundaryServiceUp] = useState("loading");
  const [npsWildfireRiskFacilityServiceUp,setNpsWildfireRiskFacilityServiceUp] = useState("loading");
  const [npsWildfireRiskCommunityServiceUp,setNpsWildfireRiskCommunityServiceUp] = useState("loading");
  const [wfftsServiceUp,setWfftsServiceUp] = useState("loading");
  const [completedTreatmentsServiceUp,setCompletedTreatmentsServiceUp] = useState("loading");
  const geoType = plannedFeature?.attributes[FieldNames.featureFields.joinFeature];
  const config = useSelector((state) => state.configuration.config);
  const anchorRefAppBar = useRef(null);
  const [showHeaderNotification, setShowHeaderNotification] = useState(false);
  const notificationMessage = "ATTENTION: IFPRS will be down on March 4, 2025 for the 1.7 Release (includes: Post Fire, BOR, and Fuels updates)."
  const { showGlobalLoader, setShowGlobalLoader } = useIFPRSGlobalContext();
  const [filterPanelOpen, setFilterPanelOpen] = useState(false);
  const activeFilterCountValue = useSelector((state) => state.filter.activeFilterCount);
  const customFilterCount = useSelector((state) => state.filter.customFilterCount);
  const [filtersActive, setFiltersActive] =  useState(false)

  useEffect(() => {
    if(userInfo){
      if(userInfo[FieldNames.userFields.isAdmin] === FieldNames.yesNo.yes)
      {
        setIsUserAdmin(true);
      }
      else
      {
        setIsUserAdmin(false);
      }
      if(userInfo[FieldNames.userFields.isElevatedReports] === FieldNames.yesNo.yes)
      {
        setIsUserElevatedReporting(true);
      }
      else
      {
        setIsUserElevatedReporting(false);
      }
    } else {
      setIsUserAdmin(false);
      setIsUserElevatedReporting(false);
    }
  }, [userInfo])

  useEffect(() => {
    let currStatus = "success"
    if(internalServiceUp === "loading" || jurisdictionalServiceUp === "loading" ||
    gaccServiceUp === "loading" ||
    stateServiceUp === "loading" || countyServiceUp === "loading" ||
    congDistrictServiceUp === "loading" || vdepImageServiceUp === "loading" ||
    tribeServiceUp === "loading" || tribeLeaderServiceUp === "loading" ||
    blmRegionServiceUp === "loading" || blmDistrictServiceUp === "loading" ||
    blmFieldServiceUp === "loading" || blmOtherServiceUp === "loading" ||
    fwsRegionServiceUp === "loading" || borRegionServiceUp === "loading" ||
    fwsRefugeServiceUp === "loading" || doiUnifiedRegionServiceUp === "loading" ||
    npsLegacyRegionsServiceUp === "loading" || npsFireManagementZoneServiceUp === "loading" ||
    npsBoundaryServiceUp === "loading" || npsWildfireRiskCommunityServiceUp === "loading" ||
    npsWildfireRiskFacilityServiceUp === "loading" || wfftsServiceUp === "loading" ||
    completedTreatmentsServiceUp === "loading")
    {
      currStatus = "loading";
    }
    if(internalServiceUp === "failure" || jurisdictionalServiceUp === "failure" ||
    gaccServiceUp === "failure" ||
    stateServiceUp === "failure" || countyServiceUp === "failure" ||
    congDistrictServiceUp === "failure" || vdepImageServiceUp === "failure" ||
    tribeServiceUp === "failure" || tribeLeaderServiceUp === "failure" ||
    blmRegionServiceUp === "failure" || blmDistrictServiceUp === "failure" ||
    blmFieldServiceUp === "failure" || blmOtherServiceUp === "failure" ||
    fwsRegionServiceUp === "failure" || borRegionServiceUp === "failure" ||
    fwsRefugeServiceUp === "failure" || doiUnifiedRegionServiceUp === "failure" ||
    npsLegacyRegionsServiceUp === "failure" || npsFireManagementZoneServiceUp === "failure" ||
    npsBoundaryServiceUp === "failure" || npsWildfireRiskCommunityServiceUp === "failure" ||
    npsWildfireRiskFacilityServiceUp === "failure" || wfftsServiceUp === "failure" ||
    completedTreatmentsServiceUp === "failure")
    {
      currStatus = "failure";
    }
    setServiceValidatorParentStatus(currStatus);
  }, [blmDistrictServiceUp, blmFieldServiceUp, blmOtherServiceUp, blmRegionServiceUp, borRegionServiceUp, completedTreatmentsServiceUp, 
    congDistrictServiceUp, countyServiceUp, doiUnifiedRegionServiceUp, fwsRefugeServiceUp, fwsRegionServiceUp,
     gaccServiceUp, internalServiceUp, jurisdictionalServiceUp, npsBoundaryServiceUp,
      npsFireManagementZoneServiceUp, npsLegacyRegionsServiceUp, npsWildfireRiskCommunityServiceUp, 
      npsWildfireRiskFacilityServiceUp, stateServiceUp, tribeLeaderServiceUp, tribeServiceUp, 
      vdepImageServiceUp, wfftsServiceUp])

  useEffect(() => {
    setEditablePerimeter(feature);
    if(anchorRefAppBar && anchorRefAppBar.current){
      const appBarHeight = ReactDOM.findDOMNode(anchorRefAppBar.current).offsetHeight;
      //setHeaderHeight(appBarHeight)
    }
    //setHeaderHeight(ReactDOM.findDOMNode(anchorRefAppBar).offsetHeight)
  }, [feature, isTreatment, setHeaderHeight]);
  useEffect(() => {
    setFiltersActive(activeFilterCountValue > 0 || customFilterCount > 0)
  }, [activeFilterCountValue, customFilterCount]);
  useEffect(() => {
    if (!browserWarningAcknowledged && !(window.navigator.userAgent.indexOf('Chrome') !== -1 
    || (!!window.chrome && (!!window.chrome.webstore || !!window.chrome.runtime)))
    && !(window.navigator.userAgent.indexOf('Edge') !== -1 || 
    (!(/*@cc_on!@*/false || !!document.documentMode) && !!window.StyleMedia))){
      setBrowserWarningOpen(true);
    } 
  }, [browserWarningAcknowledged]);

  const handleConfirmPerimeterEdit = (currentFeature) => {
    emitCustomEvent(Events.GetModifiedShape, (shape) => {
      projection.load().then(function()
      {
        const originGeometry = projection.project(shape.geometry, new SpatialReference({wkid: 4269 })); 
        let tempFeature = currentFeature;
        tempFeature.geometry = originGeometry;
        setEditablePerimeter(tempFeature);
        emitCustomEvent(Events.ModifiedGraphicChanged, { geometry: tempFeature.geometry });

        emitCustomEvent(Events.GetWFFTSAttributes, (wfftsAttributes) => {
          if(wfftsAttributes){
            rederiveFeatureInfo(tempFeature, isTreatment, isActuals, false, wfftsAttributes, null, setShowGlobalLoader)
            emitCustomEvent(Events.CompleteDrawMode);
          } else {
            emitCustomEvent(Events.GetLegacyAttributes, (legacyAttributes) => {
              if(legacyAttributes && isActuals){
                rederiveFeatureInfo(tempFeature, isTreatment, isActuals, false, null, legacyAttributes, setShowGlobalLoader)
                emitCustomEvent(Events.CompleteDrawMode);
              } else {
                rederiveFeatureInfo(tempFeature, isTreatment, isActuals, false, null, null, setShowGlobalLoader)
                emitCustomEvent(Events.CompleteDrawMode);
              }
            });
          }
        });
          
        //Set back to default
        emitCustomEvent(Events.DisableExistingIcons, true);
        emitCustomEvent(Events.DisableNewActualIcons, true);
        emitCustomEvent(Events.DisableNewEstimatedIcons, false);
      }).catch(() => { 
        setShowGlobalLoader(false);
      }).finally(() => { });

      //const wgsGeometry = webMercatorUtils.webMercatorToGeographic(geometryJsonUtils.fromJSON(shape.geometry));
      
      
      //Need to derive and adjust the editable treatment and activity
    });
  }

  const handleConfirmPointPerimeterEdit = (currentFeature) => {
    let tempFeature = currentFeature;
    setEditablePerimeter(tempFeature);
    emitCustomEvent(Events.ModifiedGraphicChanged, { geometry: tempFeature.geometry })
    //Since we are in the handleConfirmPointPerimeterEdit this will always be a point
    rederiveFeatureInfo(tempFeature, isTreatment, isActuals, true, null, null,setShowGlobalLoader);
    emitCustomEvent(Event.CompleteDrawMode);
    //Set back to default
    emitCustomEvent(Events.DisableExistingIcons, true);
    emitCustomEvent(Events.DisableNewActualIcons, true);
    emitCustomEvent(Events.DisableNewEstimatedIcons, false);
  }

  const handleConfirmPointPostFirePerimeterEdit = (currentFeature) => {
    let tempFeature = currentFeature;
    setEditablePerimeter(tempFeature);
    emitCustomEvent(Events.ModifiedGraphicPostFirePointChanged, { geometry: tempFeature.geometry })
    rederiveFeatureInfo(tempFeature, isTreatment, isActuals, true, null, null, setShowGlobalLoader);
    emitCustomEvent(Event.CompleteDrawMode);
    //Set back to default
    emitCustomEvent(Events.DisableExistingIcons, true);
    emitCustomEvent(Events.DisableNewActualIcons, true);
    emitCustomEvent(Events.DisableNewEstimatedIcons, false);
  }

  const handleConfirmLinePostFirePerimeterEdit = (currentFeature) => {
    emitCustomEvent(Events.GetModifiedShape, (shape) => {
      projection.load().then(function()
      {
        const originGeometry = projection.project(shape.geometry, new SpatialReference({wkid: 4269 })); 
        let tempFeature = currentFeature;
        tempFeature.geometry = originGeometry;
        setEditablePerimeter(tempFeature);
        emitCustomEvent(Events.ModifiedGraphicLineChanged, { geometry: tempFeature.geometry });
        rederiveFeatureInfo(tempFeature, isTreatment, isActuals, true, null, null, setShowGlobalLoader)
        emitCustomEvent(Event.CompleteDrawMode);
          
        //Set back to default
        emitCustomEvent(Events.DisableExistingIcons, true);
        emitCustomEvent(Events.DisableNewActualIcons, true);
        emitCustomEvent(Events.DisableNewEstimatedIcons, false);
      }).catch(() => { 
        setShowGlobalLoader(false);
      }).finally(() => { });

      //const wgsGeometry = webMercatorUtils.webMercatorToGeographic(geometryJsonUtils.fromJSON(shape.geometry));
      
      
      //Need to derive and adjust the editable treatment and activity
    });
  }

  const handlePerimeterReset = () => {
    if(isActuals){
      window.api.getActionByObjectID(editablePerimeter.attributes[FieldNames.featureFields.objectID], editablePerimeter.attributes[FieldNames.featureFields.joinFeature]).then(eaFeature =>{
        setEditablePerimeter(eaFeature);
        emitCustomEvent(Events.ModifiedGraphicChanged, { geometry: eaFeature.geometry })
        emitCustomEvent(Events.EnableDrawMode, { geometry: eaFeature.geometry, attributes: eaFeature.attributes });
      })
    } else {
      window.api.getActionByObjectID(editablePerimeter.attributes[FieldNames.featureFields.objectID], editablePerimeter.attributes[FieldNames.featureFields.joinFeature]).then(eaFeature =>{
        setEditablePerimeter(eaFeature);
        emitCustomEvent(Events.ModifiedGraphicChanged, { geometry: eaFeature.geometry })
        emitCustomEvent(Events.EnableDrawMode, { geometry: eaFeature.geometry, attributes: eaFeature.attributes });
      })
    }
  }

  const handleClick = (event) => {
      setAnchorEl(event.currentTarget);
  };

  const handleCreateClick = (event) => {
    setAnchorCreateMenuEl(event.currentTarget);
};

  const handleWorkflowClick = (event) => {
    setAnchorWorkflowMenuEl(event.currentTarget);
  }

  const cancelPerimeterCreate = (plannedOrActual) => {
    if (plannedOrActual === "planned") {
      setIsCreatingPlannedFeature(false);
    } else if (plannedOrActual === "actual") {
      setIsCreatingActualFeature(false);
    }
    emitCustomEvent(Events.ClearModifiedLayer);
    emitCustomEvent(Events.DisableExistingIcons, true);
    emitCustomEvent(Events.DisableNewActualIcons, true);
    emitCustomEvent(Events.DisableNewEstimatedIcons, false);
  }
  
  const handleOnDrawComplete = (itemClass) => 
  {
    setIsCreatingPlannedFeature(false);
    setIsCreatingPostFire(false);
  };

  const handleClose = () => {
      setAnchorEl(null);
  };

  const handleBrowserAlertClose = () => {
    setBrowserWarningOpen(false);
    setBrowserWarningAcknowledged(true);
  }

  const handleServiceValidatorClose = () => {
    setServiceValidatorOpen(false);
  }

  const validateServices = () => {
    setInternalServiceUp("loading");
    setJurisdictionalServiceUp("loading");
    setGaccServiceUp("loading");
    setStateServiceUp("loading");
    setCountyServiceUp("loading");
    setCongDistrictServiceUp("loading");
    setVdepImageServiceUp("loading");
    setBiaServiceUp("loading");
    setTribeServiceUp("loading");
    setTribeLeaderServiceUp("loading");
    setBlmRegionServiceUp("loading");
    setBorRegionServiceUp("loading");
    setBlmDistrictServiceUp("loading");
    setBlmFieldServiceUp("loading");
    setBlmOtherServiceUp("loading");
    setFwsRefugeServiceUp("loading");
    setFwsRefugeServiceUp("loading");
    setDoiUnifiedRegionServiceUp("loading");
    setNpsLegacyRegionsServiceUp("loading");
    setNpsFireManagementZoneServiceUp("loading");
    setNpsBoundaryServiceUp("loading");
    setNpsWildfireRiskFacilityServiceUp("loading");
    setNpsWildfireRiskCommunityServiceUp("loading");
    setWfftsServiceUp("loading");
    setCompletedTreatmentsServiceUp("loading");

    const fundingSources = DetailedFundingSources.fundingSources;
    setInternalServiceUp((fundingSources && fundingSources.length > 0) ? "success" : "failure")
    
    genericQueryExistenceCheck(config.jurisdictional.url, setJurisdictionalServiceUp);
    genericQueryExistenceCheck(config.gacc.url, setGaccServiceUp);
    genericQueryExistenceCheck(config.state.url, setStateServiceUp);
    genericQueryExistenceCheck(config.county.url, setCountyServiceUp, "OID");
    genericQueryExistenceCheck(config.congressionalDistrict.url, setCongDistrictServiceUp);

    new Request().makeRequest({
        url: config.vdepImageService.conusUrl,
        method: 'get',
        data: {
            f: 'json',
            geometryType: 'esriGeometryPolygon',
            // Test geometry created on Test, using CONUS wkid
            geometry: '{"spatialReference":{"wkid":5070},"rings":[[[336478.18724558555,2569975.9182616165],[334645.26016841666,2569977.0475441515],[336498.51475120813,2571395.532545493],[338658.11095111456,2570308.861514791],[336574.8422324544,2568328.256086083],[336478.18724558555,2569975.9182616165]]]}'
        }
    }).then(
        resp => {
          if(resp && resp.statistics && resp.statistics.length > 0){
            setVdepImageServiceUp("success");
          } else {
            setVdepImageServiceUp("failure");
          }
        },
        error => {
          setVdepImageServiceUp("failure");
          //log
        }
    ).catch(error => {
      setVdepImageServiceUp("failure");
      //log
    });

    if(config){
      genericQueryExistenceCheck(config.BIARegion.url, setBiaServiceUp, "REGION_NAM");
      genericQueryExistenceCheck(config.tribe.url, setTribeServiceUp);
      genericQueryExistenceCheck(config.tribeLeaders.url, setTribeLeaderServiceUp);
      genericQueryExistenceCheck(config.BORRegion.url, setBorRegionServiceUp, config.BORRegion.outField);
      genericQueryExistenceCheck(config.BLMDistrict.url, setBlmDistrictServiceUp);
      genericQueryExistenceCheck(config.BLMField.url, setBlmFieldServiceUp);
      genericQueryExistenceCheck(config.BLMOther.url, setBlmOtherServiceUp);
      genericQueryExistenceCheck(config.BLMRegion.url, setBlmRegionServiceUp);
      genericQueryExistenceCheck(config.FWSRegion.url, setFwsRegionServiceUp);
      genericQueryExistenceCheck(config.FWSRefuge.url, setFwsRefugeServiceUp);
      genericQueryExistenceCheck(config.DOIUnifiedRegions.url, setDoiUnifiedRegionServiceUp);
      genericQueryExistenceCheck(config.NPSLegacyRegions.url, setNpsLegacyRegionsServiceUp, "Region");
      genericQueryExistenceCheck(config.NPSFireManagementZones.url, setNpsFireManagementZoneServiceUp);
      genericQueryExistenceCheck(config.NPSBoundary.url, setNpsBoundaryServiceUp);
      genericQueryExistenceCheck(config.NPSWildfireRiskFacility.url, setNpsWildfireRiskFacilityServiceUp);
      genericQueryExistenceCheck(config.NPSWildfireRiskCommunity.url, setNpsWildfireRiskCommunityServiceUp);
      genericQueryExistenceCheck(config.WFFTS[config.env].url, setWfftsServiceUp);
      genericQueryExistenceCheck(config.completedTreatments.url, setCompletedTreatmentsServiceUp);
    }
  }

  const genericQueryExistenceCheck = (url, serviceValMethod, outField = "OBJECTID") => {
    const queryObject = new Query({
      where: "(" + outField + " IS NOT NULL)",
      outFields: outField,
      num: 1
    });

    query.executeQueryJSON(url, queryObject).then(results => {
      if(results.features && results.features.length > 0) {
        serviceValMethod("success");
      } 
      else
      {
        //The WFFTS training service is usually returning 0 features - even though it's up
        if(config.env !== "prod" && url === config.WFFTS[config.env].url && results.features.length === 0){
          serviceValMethod("success");
        } else {
          serviceValMethod("failure");
        }
      }
    }, function(error){
      serviceValMethod("failure");
    });
  }

  const serviceMessageReturn = (serviceUpValue) => {
    return(
    serviceUpValue && serviceUpValue === "loading"
    ? 
    <CircularProgress display={"inline"}/>
    :
    serviceUpValue && serviceUpValue === "success"
    ?
    <Typography color={"green"} display={"inline"} style={{ padding: '0 5px 5px 5px', margin: 0, width: '40px'}}>
      Success
    </Typography> : 
    <Typography color={"red"} display={"inline"} style={{ padding: '0 5px 5px 5px', margin: 0, width: '40px'}}>
      Failure
    </Typography>
    )
  }

  const handleCreateUserClose = () => {
    setIsCreateUserOpen(false);
  }

  const handleProfileClose = () => {
    setIsProfileOpen(false);
  }

  const handleCreateClose = () => {
    setAnchorCreateMenuEl(null);
  };

  const handleWorkflowClose = () => {
    setAnchorWorkflowMenuEl(null);
  }

  const handleCreateFuelsTreatment = () => {
    setFeature(null);
    navigate("/home");
    setShowMap(true);
    setIsCreatingPlannedFeature(true);
    setIsCreatingPostFireProject(false);
    setIsCreatingPostFire(false);
    setIsTreatment(true);
  }

  const handleCreateFuelsActivity = () => {
    setFeature(null);
    navigate("/home");
    setShowMap(true);
    setIsCreatingPlannedFeature(true);
    setIsCreatingPostFireProject(false);
    setIsCreatingPostFire(false);
    setIsTreatment(false);
  }

  const handleCreatePostFireTreatment = () => {
    setFeature(null);
    navigate("/home");
    setShowMap(true);
    setIsCreatingPlannedFeature(true);
    setIsCreatingPostFireProject(false);
    setIsCreatingPostFire(true);
    setIsTreatment(true);
  }

  const handleCreatePostFireActivity = () => {
    setFeature(null);
    navigate("/home");
    setShowMap(true);
    setIsCreatingPlannedFeature(true);
    setIsCreatingPostFireProject(false);
    setIsCreatingPostFire(true);
    setIsTreatment(false);
  }

  const handleCreatePostFireProject = () => {
    setFeature(null);
    navigate("/home");
    setIsCreatingPostFireProject(true);
    setShowMap(true);
    setIsCreatingPlannedFeature(true);
    setIsCreatingPostFire(true);
    setIsTreatment(false);
  }

  const onReplacePerimeter = () => {
    emitCustomEvent(Events.DisableExistingIcons, false);
    emitCustomEvent(Events.DisableNewActualIcons, true);
    emitCustomEvent(Events.DisableNewEstimatedIcons, true);
  }

  const onReplaceNewActualPerimeter = () => {
    emitCustomEvent(Events.SetActualIsNew, true);
    emitCustomEvent(Events.SetActualIsTreatment, isTreatment);
    emitCustomEvent(Events.DisableExistingIcons, true);
    emitCustomEvent(Events.DisableNewActualIcons, false);
    emitCustomEvent(Events.DisableNewEstimatedIcons, true);
    emitCustomEvent(Events.SetPlannedFeatureAttributes, plannedFeature.attributes)
  }

  return (
    <AppBar position="fixed" ref={anchorRefAppBar} sx={{zIndex:9999}}>
      <div>
      {createPortal(
        <Backdrop
          sx={{ color: ColorConstants.WhiteAlien, zIndex: "1350"}}
          open={showGlobalLoader}
        >
          <CircularProgress color="inherit" />
        </Backdrop>,
        document.body
      )}
      </div>
      <Toolbar style={appBarStyle(config)} sx={{height: headerHeight}}>
        <Typography variant="div" component="div" style={{ display: 'flex', flexDirection: 'row', minWidth: '560px'}}>
          <Typography variant="h6" style={{ padding: '0 5px 0 0', margin: 0, width: '250px'}}>
            <span style={{display: "flex"}}>
              <img src={logo} height="110px" alt="IFPRS" style={{ padding: "4px"}} />
              <span style={{ alignContent: "center", color: "black", fontSize: "12px", marginTop: 17}}>(Interior Fuels and Post-fire Reporting System, a module of InFORM)</span>
            </span>
          </Typography>
          <Divider orientation="vertical" variant="middle" flexItem style={{ margin: '0 5px', backgroundColor: ColorConstants.LightGrayAlien}} />
          <Button
            ref={anchorCreateMenuEl}
            id="composition-button"
            aria-controls={createOpen ? 'composition-menu' : undefined}
            aria-expanded={createOpen ? 'true' : undefined}
            aria-haspopup="true"
            onClick={handleCreateClick}
            style={{ color: ColorConstants.WhiteAlien, padding: '5px 0px 0 10px', margin: 0 }}
          > 
            { !createOpen &&
              <span style={{ display: 'flex', flexDirection: 'row' }}>Create <KeyboardArrowDownIcon /></span>
            }
            { createOpen &&
              <span style={{ display: 'flex', flexDirection: 'row' }}>Create <KeyboardArrowUpIcon /></span>
            }
          </Button>
          <CreateMenu actionFuelsTreatmentClicked={handleCreateFuelsTreatment} actionFuelsActivityClicked={handleCreateFuelsActivity} actionPostFireTreatmentClicked={handleCreatePostFireTreatment} actionPostFireActivityClicked={handleCreatePostFireActivity} 
            actionPostFireProjectClicked={handleCreatePostFireProject} anchorEl={anchorCreateMenuEl} open={createOpen} handleClose={handleCreateClose} userInfo={userInfo} cancelPerimeterCreate={() => {cancelPerimeterCreate('planned')}}/>
          <Button
            ref={anchorWorkflowMenuEl}
            id="composition-button"
            aria-controls={workflowOpen ? 'composition-menu' : undefined}
            aria-expanded={workflowOpen ? 'true' : undefined}
            aria-haspopup="true"
            onClick={handleWorkflowClick}
            style={{ color: ColorConstants.WhiteAlien, padding: '5px 0px 0 10px', marginLeft: 0}}
          > 
            { !workflowOpen &&
              <span style={{ display: 'flex', flexDirection: 'row' }}>Workflow <KeyboardArrowDownIcon /></span>
            }
            { workflowOpen &&
              <span style={{ display: 'flex', flexDirection: 'row' }}>Workflow <KeyboardArrowUpIcon /></span>
            }
          </Button>
          <WorkflowMenu anchorEl={anchorWorkflowMenuEl} open={workflowOpen} handleClose={handleWorkflowClose} isUserElevatedReporting={isUserElevatedReporting} />
          <Button
            id="composition-button"
            aria-haspopup="true"
            onClick={() => { setFilterPanelOpen(true) }}
            style={{ color: ColorConstants.WhiteAlien, padding: '5px 10px 0 10px', marginLeft: 0}}
          >
              <span style={{ display: 'flex', flexDirection: 'row' }}>Filters {filtersActive && <FilterAltIcon />}</span>
          </Button>
          {
            filterPanelOpen && 
            <WorkflowFilterContainer
              isOpen={filterPanelOpen}
              setIsOpen={setFilterPanelOpen}
              userInfo={userInfo}
              setIsCreatingPlannedFeature={setIsCreatingPlannedFeature}
              setIsCreatingActualFeature={setIsCreatingActualFeature}
            />
          }
        </Typography>
        {config.env !== "prod" &&
          <Typography variant="h5" style={{ padding: '0 5px 0 0', margin: 0, textAlign: 'center', width: '100%'}}>
            {
              config.env === "loadTesting" ? "Load Testing" 
              : config.env === "stage" ? "Staging - Release Testing" 
              : config.env === "test" ? "Test (Sprint Testing)" : 
              "DEVELOPMENT"}
          </Typography>
        }
        <div>
          {packageInfo && packageInfo.version &&
            <Typography variant="p" style={{ padding: '0 5px 0 0', margin: 0, textAlign: 'center', width: '100%'}}>
              Version: {packageInfo.version}
            </Typography>
          }
          <IconButton
              size="large"
              edge="start"
              color="inherit"
              aria-label="Actions"
              id="basic-button"
              aria-controls="basic-menu"
              aria-haspopup="true"
              aria-expanded={open ? 'true' : undefined}
              onClick={handleClick}
              sx={{ mr: 2 }}>
              <MenuIcon />
          </IconButton>
        </div>
      </Toolbar>
      {/* <Snackbar 
        open={showHeaderNotification} autoHideDuration={6000}
        anchorOrigin={{ vertical: "top", horizontal: "center" }}
        sx={{minWidth: 'auto'}}
        >
        <Alert 
          onClose={() => {
            setShowHeaderNotification(false);
          }} 
          severity={"warning"} 
          sx={{ width: '100%' }}
        >
          {notificationMessage}
        </Alert>
      </Snackbar> */}
      <ActionsMenu actionClicked={handleClose} anchorEl={anchorEl} open={open}
       handleClose={handleClose} isUserAdmin={isUserAdmin} setIsCreateUserOpen={setIsCreateUserOpen} 
       setIsProfileOpen={setIsProfileOpen} setServiceValidatorOpen={setServiceValidatorOpen} logout={logout} />
      <AdminDialog open={isCreateUserOpen} onClose={handleCreateUserClose} currentUserInfo={userInfo} 
                    setCurrentUserInfo={setUserInfo} setCurrentUserRoleInfo={setUserRoleInfo}/>
      {isProfileOpen &&
        <ProfileDialog open={isProfileOpen} onClose={handleProfileClose} userInfo={userInfo} userRoleInfo={userRoleInfo} />
      }

      <div style={{ top: 64 }}>
      <InFormAlert 
          openFlag={browserWarningOpen} 
          closeMethod={handleBrowserAlertClose}
          buttonMethod={handleBrowserAlertClose}
          titleText={"Unsupported Browser"}
          contentText={"InFORM supports Edge and Chrome. Please use one of these two browsers."}
        />
      <InFormGenericModal
        openFlag={serviceValidatorOpen}
        openMethod={validateServices}
        closeMethod={handleServiceValidatorClose}
        buttonMethod={validateServices}
        titleText={"Validate InForm Fuels Service Connections"}
        buttonText={"Refresh"}
        children={null}
        width={'auto'}
      >
        <Box style={{maxHeight: '300px', overflow: 'auto', width: '33%',minWidth: '450px'}}>
          <Typography variant="h6" style={{ padding: '0 5px 5px 5px', margin: 0, width: '400px'}} sx={{fontWeight:"bold"}}>
            Overall Status: { serviceMessageReturn(serviceValidatorParentStatus) }
          </Typography>
          <Divider orientation="horizontal" variant="middle" flexItem style={{ margin: '0 10px', backgroundColor: ColorConstants.LightGrayAlien}} />
          <Typography variant="h6" style={{ padding: '0 5px 5px 5px', margin: 0, width: '400px'}}>
            InForm Fuels Internal Service
          </Typography>
          { serviceMessageReturn(internalServiceUp) }
          <Divider orientation="horizontal" variant="middle" flexItem style={{ margin: '0 10px', backgroundColor: ColorConstants.LightGrayAlien}} />
          <Typography variant="h6" style={{ padding: '0 5px 5px 5px', margin: 0, width: '400px'}}>
            Jurisdictional Service
          </Typography>
          { serviceMessageReturn(jurisdictionalServiceUp) }
          <Divider orientation="horizontal" variant="middle" flexItem style={{ margin: '0 10px', backgroundColor: ColorConstants.LightGrayAlien}} />
          <Divider orientation="horizontal" variant="middle" flexItem style={{ margin: '0 10px', backgroundColor: ColorConstants.LightGrayAlien}} />
          <Typography variant="h6" style={{ padding: '0 5px 5px 5px', margin: 0, width: '400px'}}>
            GACC Service
          </Typography>
          { serviceMessageReturn(gaccServiceUp) }
          <Divider orientation="horizontal" variant="middle" flexItem style={{ margin: '0 10px', backgroundColor: ColorConstants.LightGrayAlien}} />
          <Typography variant="h6" style={{ padding: '0 5px 5px 5px', margin: 0, width: '400px'}}>
            State Service
          </Typography>
          { serviceMessageReturn(stateServiceUp) }
          <Divider orientation="horizontal" variant="middle" flexItem style={{ margin: '0 10px', backgroundColor: ColorConstants.LightGrayAlien}} />
          <Typography variant="h6" style={{ padding: '0 5px 5px 5px', margin: 0, width: '400px'}}>
            County Service
          </Typography>
          { serviceMessageReturn(countyServiceUp) }
          <Divider orientation="horizontal" variant="middle" flexItem style={{ margin: '0 10px', backgroundColor: ColorConstants.LightGrayAlien}} />
          <Typography variant="h6" style={{ padding: '0 5px 5px 5px', margin: 0, width: '400px'}}>
            Congressional District Service
          </Typography>
          { serviceMessageReturn(congDistrictServiceUp) }
          <Divider orientation="horizontal" variant="middle" flexItem style={{ margin: '0 10px', backgroundColor: ColorConstants.LightGrayAlien}} />
          <Typography variant="h6" style={{ padding: '0 5px 5px 5px', margin: 0, width: '400px'}}>
            BIA Service
          </Typography>
          { serviceMessageReturn(biaServiceUp) }
          <Divider orientation="horizontal" variant="middle" flexItem style={{ margin: '0 10px', backgroundColor: ColorConstants.LightGrayAlien}} />
          <Typography variant="h6" style={{ padding: '0 5px 5px 5px', margin: 0, width: '400px'}}>
            Image Service (vdep)
          </Typography>
          { serviceMessageReturn(vdepImageServiceUp) }
          <Divider orientation="horizontal" variant="middle" flexItem style={{ margin: '0 10px', backgroundColor: ColorConstants.LightGrayAlien}} />
          <Typography variant="h6" style={{ padding: '0 5px 5px 5px', margin: 0, width: '400px'}}>
            Tribe Service
          </Typography>
          { serviceMessageReturn(tribeServiceUp) }
          <Divider orientation="horizontal" variant="middle" flexItem style={{ margin: '0 10px', backgroundColor: ColorConstants.LightGrayAlien}} />
          <Typography variant="h6" style={{ padding: '0 5px 5px 5px', margin: 0, width: '400px'}}>
            Tribe Leader Service
          </Typography>
          { serviceMessageReturn(tribeLeaderServiceUp) }
          <Divider orientation="horizontal" variant="middle" flexItem style={{ margin: '0 10px', backgroundColor: ColorConstants.LightGrayAlien}} />
          <Typography variant="h6" style={{ padding: '0 5px 5px 5px', margin: 0, width: '400px'}}>
            BLM Region Service
          </Typography>
          { serviceMessageReturn(blmRegionServiceUp) }
          <Divider orientation="horizontal" variant="middle" flexItem style={{ margin: '0 10px', backgroundColor: ColorConstants.LightGrayAlien}} />
          <Typography variant="h6" style={{ padding: '0 5px 5px 5px', margin: 0, width: '400px'}}>
            BLM District Service
          </Typography>
          { serviceMessageReturn(blmDistrictServiceUp) }
          <Divider orientation="horizontal" variant="middle" flexItem style={{ margin: '0 10px', backgroundColor: ColorConstants.LightGrayAlien}} />
          <Typography variant="h6" style={{ padding: '0 5px 5px 5px', margin: 0, width: '400px'}}>
            BLM Field Unit Service
          </Typography>
          { serviceMessageReturn(blmFieldServiceUp) }
          <Divider orientation="horizontal" variant="middle" flexItem style={{ margin: '0 10px', backgroundColor: ColorConstants.LightGrayAlien}} />
          <Typography variant="h6" style={{ padding: '0 5px 5px 5px', margin: 0, width: '400px'}}>
            BLM Other Unit Service
          </Typography>
          { serviceMessageReturn(blmOtherServiceUp) }
          <Divider orientation="horizontal" variant="middle" flexItem style={{ margin: '0 10px', backgroundColor: ColorConstants.LightGrayAlien}} />
          <Typography variant="h6" style={{ padding: '0 5px 5px 5px', margin: 0, width: '400px'}}>
            BOR Region Service
          </Typography>
          { serviceMessageReturn(borRegionServiceUp) }
          <Divider orientation="horizontal" variant="middle" flexItem style={{ margin: '0 10px', backgroundColor: ColorConstants.LightGrayAlien}} />
          <Typography variant="h6" style={{ padding: '0 5px 5px 5px', margin: 0, width: '400px'}}>
            FWS Region Service
          </Typography>
          { serviceMessageReturn(fwsRegionServiceUp) }
          <Divider orientation="horizontal" variant="middle" flexItem style={{ margin: '0 10px', backgroundColor: ColorConstants.LightGrayAlien}} />
          <Typography variant="h6" style={{ padding: '0 5px 5px 5px', margin: 0, width: '400px'}}>
            FWS Refuge Service
          </Typography>
          { serviceMessageReturn(fwsRefugeServiceUp) }
          <Divider orientation="horizontal" variant="middle" flexItem style={{ margin: '0 10px', backgroundColor: ColorConstants.LightGrayAlien}} />
          <Typography variant="h6" style={{ padding: '0 5px 5px 5px', margin: 0, width: '400px'}}>
            DOI Unified Regions Service
          </Typography>
          { serviceMessageReturn(doiUnifiedRegionServiceUp) }
          <Divider orientation="horizontal" variant="middle" flexItem style={{ margin: '0 10px', backgroundColor: ColorConstants.LightGrayAlien}} />
          <Typography variant="h6" style={{ padding: '0 5px 5px 5px', margin: 0, width: '400px'}}>
            NPS Legacy Regions Service
          </Typography>
          { serviceMessageReturn(npsLegacyRegionsServiceUp) }
          <Divider orientation="horizontal" variant="middle" flexItem style={{ margin: '0 10px', backgroundColor: ColorConstants.LightGrayAlien}} />
          <Typography variant="h6" style={{ padding: '0 5px 5px 5px', margin: 0, width: '400px'}}>
            NPS Fire Management Zones Service
          </Typography>
          { serviceMessageReturn(npsFireManagementZoneServiceUp) }
          <Divider orientation="horizontal" variant="middle" flexItem style={{ margin: '0 10px', backgroundColor: ColorConstants.LightGrayAlien}} />
          <Typography variant="h6" style={{ padding: '0 5px 5px 5px', margin: 0, width: '400px'}}>
            NPS Boundary Service
          </Typography>
          { serviceMessageReturn(npsBoundaryServiceUp) }
          <Divider orientation="horizontal" variant="middle" flexItem style={{ margin: '0 10px', backgroundColor: ColorConstants.LightGrayAlien}} />
          <Typography variant="h6" style={{ padding: '0 5px 5px 5px', margin: 0, width: '400px'}}>
            NPS Wildfire Risk Facility Service
          </Typography>
          { serviceMessageReturn(npsWildfireRiskFacilityServiceUp) }
          <Divider orientation="horizontal" variant="middle" flexItem style={{ margin: '0 10px', backgroundColor: ColorConstants.LightGrayAlien}} />
          <Typography variant="h6" style={{ padding: '0 5px 5px 5px', margin: 0, width: '400px'}}>
            NPS Wildfire Risk Community Service
          </Typography>
          { serviceMessageReturn(npsWildfireRiskCommunityServiceUp) }
          <Divider orientation="horizontal" variant="middle" flexItem style={{ margin: '0 10px', backgroundColor: ColorConstants.LightGrayAlien}} />
          <Typography variant="h6" style={{ padding: '0 5px 5px 5px', margin: 0, width: '400px'}}>
            WFFTS Service
          </Typography>
          { serviceMessageReturn(wfftsServiceUp) }
          <Divider orientation="horizontal" variant="middle" flexItem style={{ margin: '0 10px', backgroundColor: ColorConstants.LightGrayAlien}} />
          <Typography variant="h6" style={{ padding: '0 5px 5px 5px', margin: 0, width: '400px'}}>
            Completed Treatments Service
          </Typography>
          { serviceMessageReturn(completedTreatmentsServiceUp) }
        </Box>
      </InFormGenericModal>
      <ThemeProvider theme={theme}>
      <Snackbar open={derivationErrorOpen} autoHideDuration={6000}
        anchorOrigin={{ vertical: "top", horizontal: "center" }}
        className={classes.snackBarStyle}
        onClose={() => {
          setDerivationErrorOpen(false);
          setDerivationError("");
        }}>
        <Alert onClose={() => {
          setDerivationErrorOpen(false);
          setDerivationError("");
        }} severity="error" sx={{ width: '100%' }}>
          {derivationError}
        </Alert>
      </Snackbar>
      </ThemeProvider>
          <ThemeProvider theme={theme}>
            <Snackbar open={mapSnackbarOpen} 
              autoHideDuration={6000}
              anchorOrigin={{ vertical: "top", horizontal: "center" }}
              className={classes.snackBarStyle}
              onClose={() => {
                setMapSnackbarOpen(false);
                setMapSnackbarMessage("");
                setMapSnackbarSeverity("success");
              }}>
              <Alert onClose={() => {
                setMapSnackbarOpen(false);
                setMapSnackbarMessage("");
                setMapSnackbarSeverity("success");
              }} severity={mapSnackbarSeverity} sx={{ width: '100%' }}>
                {mapSnackbarMessage}
              </Alert>
            </Snackbar>
          </ThemeProvider>
      </div>
      
      {(isCreatingActualFeature && plannedFeature && !isCreatingPlannedFeature) &&
          <CreateActualsToolbar onCreatePerimeterCancel={() => cancelPerimeterCreate('actual')} onDrawComplete={handleOnDrawComplete} isTreatment={isTreatment}
            isCreatingActualFeature={isCreatingActualFeature} isCreatingPlannedFeature={isCreatingPlannedFeature}
            plannedFeature={plannedFeature} setIsCreatingActualFeature={setIsCreatingActualFeature} 
            userInfo={userInfo} onReplaceNewActualPerimeter={onReplaceNewActualPerimeter} isCreatingPostFire={isCreatingPostFire}
            geoType={geoType}
          />
      }

      {(isCreatingPlannedFeature && !isCreatingActualFeature) &&
          <CreatePerimeterToolbar onCreatePerimeterCancel={() => cancelPerimeterCreate('planned')} onDrawComplete={handleOnDrawComplete} isTreatment={isTreatment}
            isCreatingActualFeature={isCreatingActualFeature} isCreatingPlannedFeature={isCreatingPlannedFeature} 
            feature={feature} onReplacePerimeter={onReplacePerimeter} isCreatingPostFire={isCreatingPostFire} isCreatingProject={isCreatingPostFireProject} />
      }

      {(isCreatingPlannedFeature && isCreatingPostFireProject) &&
          <CreatePerimeterToolbar onCreatePerimeterCancel={() => cancelPerimeterCreate('planned')} onDrawComplete={handleOnDrawComplete} isTreatment={isTreatment}
            isCreatingActualFeature={isCreatingActualFeature} isCreatingPlannedFeature={isCreatingPlannedFeature} 
            feature={feature} onReplacePerimeter={onReplacePerimeter} isCreatingPostFire={isCreatingPostFire} isCreatingProject={isCreatingPostFireProject} />
      }
      
      {(mode === FieldNames.modes.edit && !isCreatingPlannedFeature && !isNewFeature && !isApproved && !isActuals && (!isProject || (isProject && feature?.attributes?.[FieldNames.projectFields.isFirePerimeter] !== FieldNames.yesNo.yes))) &&
        <EditGeometryToolbar 
          feature={feature}
          isTreatment={isTreatment}
          isActuals={isActuals}
          perimeter={editablePerimeter}
          onConfirmPerimeterEdit={handleConfirmPerimeterEdit}
          onDrawComplete={handleOnDrawComplete} 
          handlePerimeterReset={handlePerimeterReset}
          onConfirmPointPerimeterEdit={handleConfirmPointPerimeterEdit}
          onReplacePerimeter={onReplacePerimeter}
          onConfirmPointPostFirePerimeterEdit={handleConfirmPointPostFirePerimeterEdit}
          onConfirmLinePostFirePerimeterEdit={handleConfirmLinePostFirePerimeterEdit}
        />
      }

      {(mode === FieldNames.modes.edit && !isCreatingActualFeature && !isNewFeature && isActuals && isWithinFiscalYear && !isCompleted && (!isProject || (isProject && feature?.attributes?.[FieldNames.projectFields.isFirePerimeter] !== FieldNames.yesNo.yes))) &&
        <EditGeometryToolbar 
          feature={feature}
          isTreatment={isTreatment}
          isActuals={isActuals}
          perimeter={editablePerimeter}
          onConfirmPerimeterEdit={handleConfirmPerimeterEdit}
          onDrawComplete={handleOnDrawComplete} 
          handlePerimeterReset={handlePerimeterReset}
          onConfirmPointPerimeterEdit={handleConfirmPointPerimeterEdit}
          onReplacePerimeter={onReplacePerimeter}
          onConfirmPointPostFirePerimeterEdit={handleConfirmPointPostFirePerimeterEdit}
          onConfirmLinePostFirePerimeterEdit={handleConfirmLinePostFirePerimeterEdit}
        />
      }
    </AppBar>
    
  )
}